/**
 * Skills editing card (P4.T3).
 *
 * Two sections — Default skills (always loaded) + Auto-load skills
 * (loaded by intent classifier when relevant to the turn). Each section
 * has its own "+ Add skill" button opening a shared SkillPickerModal
 * with the appropriate mode.
 *
 * Reads:
 *   - draftBundle ?? serverBundle for the installed skill lists
 *   - useSkillCatalog for the platform catalog (display_name +
 *     required_capabilities lookup)
 *
 * Writes via composerStore.addSkill / removeSkill. Save flows through
 * the Workshop top bar (T2) → useUpdatePersonaBundle (T2,
 * widened in T3 to send default_skills + auto_skills).
 *
 * Catalog-load failure: card still renders installed-skill sections so
 * Diego can still remove skills; an inline Alert at the top surfaces
 * the error with a Retry button.
 *
 * Required-capabilities text badge per row is a v1 plain-text affordance;
 * P4.T9 layers a traffic-light satisfaction icon onto the same DOM by
 * reading bundle.tool_bindings — no T3 rework needed. See plan §5
 * forward-promise pin.
 */
import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { useComposerStore, type SkillMode } from '@/stores/composerStore'
import { useSkillCatalog, type SkillCatalogEntry } from '@/hooks/useSkillCatalog'
import { useBundleFindings } from '@/hooks/useBundleFindings'
import { TrafficLight } from '@/components/composer/TrafficLight'
import { ExplainPopover } from '@/components/composer/ExplainPopover'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'
import { SkillPickerModal } from './SkillPickerModal'

interface SkillListProps {
  title: string
  helper: string
  skills: { name: string }[]
  catalogByName: Map<string, SkillCatalogEntry>
  findingForSkill: (name: string) => Finding | undefined
  onAdd: () => void
  onRemove: (name: string) => void
  onJump?: (location: FindingLocation) => void
  emptyCopy: string
}

function SkillList({
  title,
  helper,
  skills,
  catalogByName,
  findingForSkill,
  onAdd,
  onRemove,
  onJump,
  emptyCopy,
}: SkillListProps) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {helper}
        </p>
      </div>
      {skills.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          {emptyCopy}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {skills.map(({ name }) => {
            const catalog = catalogByName.get(name)
            const display = catalog?.display_name ?? name
            const required = catalog?.required_capabilities ?? []
            const finding = findingForSkill(name)
            // P4.T9: skill-card findings (rules 1+2) jump to the Tools card
            // because that's where the user fixes the unbound/unregistered
            // adapter. Other rules (none today) would render without jumpTo.
            const jumpTo: FindingLocation | undefined =
              finding &&
              (finding.rule === 'skill_unsatisfied_capability' ||
                finding.rule === 'skill_bound_to_unregistered_adapter')
                ? { card: 'tools', anchorId: '' }
                : undefined
            return (
              <li
                key={name}
                className="flex items-start gap-2 p-2.5 rounded-sm border border-border"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {display}
                  </p>
                  {required.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 font-mono inline-flex items-center gap-1">
                      <span>Requires: {required.join(', ')}</span>
                      {finding && (
                        <ExplainPopover
                          title={finding.message}
                          reasoning={finding.explanation}
                          jumpTo={jumpTo}
                          onJump={onJump}
                        >
                          <button
                            type="button"
                            className="cursor-pointer ml-1 align-middle"
                            aria-label={`Explain finding for ${name}`}
                          >
                            <TrafficLight severity={finding.severity} />
                          </button>
                        </ExplainPopover>
                      )}
                    </p>
                  )}
                  {required.length === 0 && finding && (
                    <span className="inline-flex items-center mt-0.5">
                      <ExplainPopover
                        title={finding.message}
                        reasoning={finding.explanation}
                        jumpTo={jumpTo}
                        onJump={onJump}
                      >
                        <button
                          type="button"
                          className="cursor-pointer ml-1 align-middle"
                          aria-label={`Explain finding for ${name}`}
                        >
                          <TrafficLight severity={finding.severity} />
                        </button>
                      </ExplainPopover>
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(name)}
                  aria-label={`Remove ${name}`}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            )
          })}
        </ul>
      )}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus className="h-4 w-4" />}
        onClick={onAdd}
      >
        Add skill
      </Button>
    </section>
  )
}

interface SkillsCardProps {
  /**
   * P4.T9: optional scroll-and-flash handler from the parent
   * Workshop page. When set, ExplainPopover renders a "Jump to
   * <card>" button that fires this with the rule-specific target.
   */
  onJump?: (location: FindingLocation) => void
}

export function SkillsCard({ onJump }: SkillsCardProps = {}) {
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const removeSkill = useComposerStore((s) => s.removeSkill)
  const catalog = useSkillCatalog()
  const findings = useBundleFindings()
  const [pickerOpen, setPickerOpen] = useState<SkillMode | null>(null)

  const base = draft ?? server
  if (!base) return null

  const catalogByName = new Map<string, SkillCatalogEntry>(
    (catalog.data ?? []).map((s) => [s.name, s]),
  )

  // P4.T9: anchorId for skill findings is `skill.name` (the same join
  // key SkillsCard already uses against bundle.default_skills[].name).
  const findingForSkill = (name: string): Finding | undefined =>
    findings.find(
      (f) => f.location.card === 'skills' && f.location.anchorId === name,
    )

  return (
    <Card className="p-6 gap-0">
      <h2 className="text-lg font-semibold mb-4">Skills</h2>

      {catalog.isError && (
        <Alert variant="destructive" className="text-xs mb-4">
          <div className="flex items-center justify-between gap-3">
            <span>
              Unable to load the skill catalog — display names + required
              capabilities won't render. Installed-skill removal still works.
            </span>
            <Button variant="ghost" size="sm" onClick={() => catalog.refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      <div className="space-y-6">
        <SkillList
          title="Default skills (always on)"
          helper="These skills are always loaded into the persona's context."
          skills={base.default_skills}
          catalogByName={catalogByName}
          findingForSkill={findingForSkill}
          onAdd={() => setPickerOpen('default')}
          onRemove={removeSkill}
          onJump={onJump}
          emptyCopy="No default skills installed. Click + Add skill to choose from the library."
        />
        <SkillList
          title="Auto-load skills (loaded when relevant to the turn)"
          helper="These skills are loaded automatically when the customer's message triggers them. Auto-load classifier is wired in a later phase; installed auto-load skills are inert until then."
          skills={base.auto_skills}
          catalogByName={catalogByName}
          findingForSkill={findingForSkill}
          onAdd={() => setPickerOpen('auto')}
          onRemove={removeSkill}
          onJump={onJump}
          emptyCopy="No auto-load skills installed. Click + Add skill to choose from the library."
        />
      </div>

      {pickerOpen !== null && (
        <SkillPickerModal
          open={true}
          mode={pickerOpen}
          onOpenChange={(open) => {
            if (!open) setPickerOpen(null)
          }}
        />
      )}
    </Card>
  )
}
